import React, {
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import {Menu} from "@headlessui/react";
import {DropdownMenuStyled, styled} from "@reside/ui";

import {shouldFlipDropdownMenuUp} from "./shouldFlipDropdownMenuUp";

type Props = Readonly<{
  trigger: (open: boolean) => ReactNode;
  closeOnScroll?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}>;

/**
 * List-table variant of `@reside/ui` DropdownMenu that flips above the trigger
 * when there is not enough room below (e.g. dashboard minimap overlap).
 */
export const ListTableDropdownMenu: FunctionComponent<Props> = ({
  trigger,
  children,
  closeOnScroll,
  onOpen,
  onClose,
}) => {
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const [openUp, setOpenUp] = useState(false);

  return (
    <Menu as={Container} data-list-table-dropdown="">
      {({close, open}) => (
        <>
          <FlipUpDetector
            open={open}
            triggerRef={triggerRef}
            onOpenUpChange={setOpenUp}
          />
          {closeOnScroll ? <CloseOnScroll close={close} /> : undefined}
          <TrackEvents open={open} onOpen={onOpen} onClose={onClose} />
          <Menu.Button as={TriggerWrap} ref={triggerRef}>
            {({open: isOpen}) => trigger(isOpen)}
          </Menu.Button>
          <Menu.Items as={openUp ? DropdownMenuOpenUp : DropdownMenuStyled}>
            {children}
          </Menu.Items>
        </>
      )}
    </Menu>
  );
};

const FlipUpDetector: FunctionComponent<{
  open: boolean;
  triggerRef: RefObject<HTMLDivElement | null>;
  onOpenUpChange: (openUp: boolean) => void;
}> = ({open, triggerRef, onOpenUpChange}) => {
  const updateFlip = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    const menu = trigger
      .closest("[data-list-table-dropdown]")
      ?.querySelector('[role="menu"]');
    onOpenUpChange(
      shouldFlipDropdownMenuUp(
        trigger,
        menu instanceof HTMLElement ? menu : null,
      ),
    );
  }, [onOpenUpChange, triggerRef]);

  useLayoutEffect(() => {
    if (!open) {
      onOpenUpChange(false);
      return;
    }

    updateFlip();
  }, [open, onOpenUpChange, updateFlip]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    window.addEventListener("resize", updateFlip);
    return () => window.removeEventListener("resize", updateFlip);
  }, [open, updateFlip]);

  return null;
};

const CloseOnScroll: FunctionComponent<
  PropsWithChildren<{close: () => void}>
> = ({close, children}) => {
  useEffect(() => {
    const handleScroll = () => {
      close();
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [close]);

  return <>{children}</>;
};

const TrackEvents: FunctionComponent<{
  open: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}> = ({open, onOpen, onClose, children}) => {
  const [isOpen, setIsOpen] = useState(open);
  useEffect(() => {
    if (open !== isOpen) {
      setIsOpen(open);
      if (!open && onClose) {
        onClose();
      }
      if (open && onOpen) {
        onOpen();
      }
    }
  }, [open, isOpen, onClose, onOpen]);
  return <>{children}</>;
};

const DropdownMenuOpenUp = styled(DropdownMenuStyled)`
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 8px;
`;

const Container = styled.div`
  display: inline-block;
  position: relative;
`;

const TriggerWrap = styled.div`
  display: inline-flex;
`;
